/**
 * Authentication Context
 * In dev mode: authenticates against backend /auth/login (no Cognito needed)
 * In production: uses AWS Cognito via Amplify v6
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

export interface User {
  id: string;
  email: string;
  name?: string;
}

interface SignUpResult {
  userConfirmed: boolean;
  message: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<SignUpResult>;
  confirmEmail: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

const TOKEN_KEY = 'awl_token';
const REFRESH_TOKEN_KEY = 'awl_refresh_token';
const USER_KEY = 'awl_user';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);

      if (savedToken && savedUser) {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      }
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    const response = await api.post<{ success: boolean; data: any }>('/auth/login', { email, password });
    const data = response.data.data;

    // Cognito mode returns { tokens: { accessToken, idToken, refreshToken }, user }
    // Dev mode returns { token, user }
    const token = data.token || data.tokens?.accessToken;
    const refreshToken = data.tokens?.refreshToken;

    localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));

    setUser(data.user);
    setIsAuthenticated(true);
  };

  const signUp = async (email: string, password: string, name: string): Promise<SignUpResult> => {
    const response = await api.post<{ success: boolean; data: SignUpResult }>('/auth/register', { email, password, name });
    return response.data.data;
  };

  const confirmEmail = async (email: string, code: string): Promise<void> => {
    await api.post('/auth/confirm', { email, code });
  };

  const resendCode = async (email: string): Promise<void> => {
    await api.post('/auth/resend-code', { email });
  };

  const signOut = async (): Promise<void> => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const getIdToken = async (): Promise<string | null> => {
    return localStorage.getItem(TOKEN_KEY);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    signUp,
    confirmEmail,
    resendCode,
    signOut,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
